import { LayoutDashboard, ShieldAlert, Building2, FileText, Map, Settings, Bell, LogOut, ChevronDown } from "lucide-react";
import React, { useState } from "react";
import { IncidentList } from "./components/IncidentList";
import { IncidentForm } from "./components/IncidentForm";
import { CaseTypeList } from "./components/CaseTypeList";
import { AgencyList } from "./components/AgencyList";
import { ReportsAnalytics } from "./components/ReportsAnalytics";
import { Dashboard } from "./components/Dashboard";
import { initialIncidents } from "./data/incidents";
import { initialCaseTypes } from "./data/caseTypes";
import { initialAgencies } from "./data/agencies";
import { Incident, IncidentInput } from "./types/incident";
import { CaseType, Agency } from "./types/masterData";
export function App() {
  const [activeMenu, setActiveMenu] = useState("Incident");
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    "Incident Management": true,
    "Settings": true
  });

  // Data State
  const [incidents, setIncidents] = useState<Incident[]>(initialIncidents);
  const [caseTypes, setCaseTypes] = useState<CaseType[]>(initialCaseTypes);
  const [agencies, setAgencies] = useState<Agency[]>(initialAgencies);

  // View State
  const [viewMode, setViewMode] = useState<'list' | 'form' | 'view'>('list');
  const [currentIncident, setCurrentIncident] = useState<Incident | undefined>(undefined);
  const toggleMenu = (menu: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };
  const handleMenuClick = (menu: string) => {
    setActiveMenu(menu);
    setViewMode('list');
    setCurrentIncident(undefined);
  };

  // Incident Handlers
  const handleAddIncident = () => {
    setCurrentIncident(undefined);
    setViewMode('form');
  };
  const handleEditIncident = (incident: Incident) => {
    setCurrentIncident(incident);
    setViewMode('form');
  };
  const handleViewIncident = (incident: Incident) => {
    setCurrentIncident(incident);
    setViewMode('view');
  };
  const handleDeleteIncident = (id: string) => {
    if (confirm('Are you sure you want to delete this incident?')) {
      setIncidents(prev => prev.filter(inc => inc.id !== id));
    }
  };
  const handleSaveIncident = (data: IncidentInput) => {
    if (currentIncident) {
      // Edit
      setIncidents(prev => prev.map(inc => inc.id === currentIncident.id ? {
        ...inc,
        ...data
      } : inc));
    } else {
      // Create
      const newIncident: Incident = {
        ...data,
        id: `INC-${new Date().getFullYear()}-${String(incidents.length + 1).padStart(4, '0')}`,
        status: 'Submitted',
        createdAt: new Date().toISOString()
      };
      setIncidents([newIncident, ...incidents]);
    }
    setViewMode('list');
  };
  const handleApprove = (id: string, remarks: string) => {
    setIncidents(prev => prev.map(inc => inc.id === id ? {
      ...inc,
      status: 'Approved',
      reviewedAt: new Date().toISOString(),
      reviewNote: remarks
    } : inc));
  };
  const handleReject = (id: string, remarks: string) => {
    setIncidents(prev => prev.map(inc => inc.id === id ? {
      ...inc,
      status: 'Rejected',
      reviewedAt: new Date().toISOString(),
      reviewNote: remarks
    } : inc));
  };

  // Settings Handlers
  const handleSaveCaseType = (type: CaseType) => {
    setCaseTypes(prev => {
      const exists = prev.find(t => t.id === type.id);
      if (exists) return prev.map(t => t.id === type.id ? type : t);
      return [...prev, type];
    });
  };
  const handleSaveAgency = (agency: Agency) => {
    setAgencies(prev => {
      const exists = prev.find(a => a.id === agency.id);
      if (exists) return prev.map(a => a.id === agency.id ? agency : a);
      return [...prev, agency];
    });
  };
  const renderContent = () => {
    if (activeMenu === 'Dashboard') {
      return <Dashboard incidents={incidents} onNavigate={handleMenuClick} data-id="element-0" data-mp="6muiwx6m88gj" />;
    }
    if (activeMenu === 'Incident') {
      if (viewMode === 'form') {
        return <IncidentForm incident={currentIncident} onSave={handleSaveIncident} onCancel={() => setViewMode('list')} data-id="element-1" data-mp="qwvsagd3qr7f" />;
      }
      if (viewMode === 'view' && currentIncident) {
        // Simple view mode for now, could be expanded into a full component
        return <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2" data-mp="h4zpis02mcwv">
            <button onClick={() => setViewMode('list')} className="mb-4 text-blue-600 hover:underline text-sm font-medium" data-id="element-3" data-mp="c66auw91sfv4">← Back to List</button>
            <h2 className="text-xl font-bold mb-4" data-id="element-4" data-mp="14wez95u6fwi5">Incident Details: {currentIncident.id}</h2>
            <pre className="bg-gray-50 p-4 rounded text-sm overflow-auto" data-id="element-5" data-mp="zxanq8js5mon">{JSON.stringify(currentIncident, null, 2)}</pre>
          </div>;
      }
      return <IncidentList incidents={incidents} onAdd={handleAddIncident} onEdit={handleEditIncident} onDelete={handleDeleteIncident} onView={handleViewIncident} data-id="element-6" data-mp="vybbe2vpynn6" />;
    }
    if (activeMenu === 'Approval') {
      return <IncidentList incidents={incidents} onAdd={handleAddIncident} onEdit={handleEditIncident} onDelete={handleDeleteIncident} onView={handleViewIncident} isApprovalView onApprove={handleApprove} onReject={handleReject} data-id="element-7" data-mp="3gotxrj37h2z2" />;
    }
    if (activeMenu === 'Reports') {
      return <ReportsAnalytics incidents={incidents} data-id="element-8" data-mp="253sd5h9iq28b" />;
    }
    if (activeMenu === 'Case Type') {
      return <CaseTypeList caseTypes={caseTypes} onSave={handleSaveCaseType} data-id="element-9" data-mp="29x4quwcnsoma" />;
    }
    if (activeMenu === 'Reporting Agency') {
      return <AgencyList agencies={agencies} onSave={handleSaveAgency} data-id="element-10" data-mp="2jtzlh45ta4va" />;
    }
    return <div className="flex items-center justify-center h-full text-gray-500" data-id="element-11" data-mp="2jvub2vs12l5p">
        <div className="text-center" data-id="element-12" data-mp="2nwe3jm1np0li">
          <h2 className="text-xl font-semibold mb-2" data-id="element-13" data-mp="2sufmm6z30zwz">{activeMenu}</h2>
          <p data-id="element-14" data-mp="2sslbfkdm4m63">This module is under construction.</p>
        </div>
      </div>;
  };
  return <div className="flex h-screen w-full bg-[#f8f9fa] font-sans overflow-hidden" data-id="element-15" data-mp="tjocrd6l47ic">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0" data-id="element-16" data-mp="yihq95lvfyii">
        <div className="h-16 flex items-center px-4 border-b border-gray-200" data-id="element-17" data-mp="21toiy3ynyz8c">
          <div className="flex items-center gap-2" data-id="element-18" data-mp="1wu25btexbgz0">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold" data-id="element-19" data-mp="2ass1n6tx5cmb">
              <ShieldAlert size={18} data-id="element-20" data-mp="25sw5tuvdkrhu" />
            </div>
            <span className="font-semibold text-sm text-gray-800 leading-tight" data-id="element-21" data-mp="25vifc01tth2p">
              Ministry of Internal<br data-id="element-22" data-mp="2ko3lg3k6pa0x" />Security
            </span>
          </div>
        </div>

        <div className="p-4 overflow-y-auto flex-1" data-id="element-23" data-mp="2fr3cnkvy9t64">
          <div className="text-xs font-semibold text-gray-400 mb-4 uppercase tracking-wider" data-id="element-24" data-mp="2tj003bmobew6">DAC MODULE</div>
          <nav className="space-y-1" data-id="element-25" data-mp="2pi7q2g5z95yu">
            <SidebarItem icon={<LayoutDashboard size={18} data-id="element-27" data-mp="2pjiw6mmwapt4" />} label="Dashboard" active={activeMenu === "Dashboard"} onClick={() => handleMenuClick("Dashboard")} data-id="element-26" data-mp="2pj9edmc07oyi" />
            
            <SidebarGroup icon={<ShieldAlert size={18} data-id="element-29" data-mp="2yg05rlfm2fmr" />} label="Incident Management" isExpanded={expandedMenus["Incident Management"]} onToggle={() => toggleMenu("Incident Management")} data-id="element-28" data-mp="2yj5toxci7bgw">
              <SidebarSubItem label="Incident" active={activeMenu === "Incident"} onClick={() => handleMenuClick("Incident")} data-id="element-30" data-mp="385ypl11yzz89" />
              <SidebarSubItem label="Approval" active={activeMenu === "Approval"} onClick={() => handleMenuClick("Approval")} data-id="element-31" data-mp="3d38n73zijh76" />
            </SidebarGroup>

            <SidebarItem icon={<FileText size={18} data-id="element-33" data-mp="344di5v87gd5g" />} label="Reports & Analytics" active={activeMenu === "Reports"} onClick={() => handleMenuClick("Reports")} data-id="element-32" data-mp="343vck7znkmkv" />
            
            <SidebarGroup icon={<Settings size={18} data-id="element-35" data-mp="2udsjk7sj78mj" />} label="Settings" isExpanded={expandedMenus["Settings"]} onToggle={() => toggleMenu("Settings")} data-id="element-34" data-mp="2pcut77xdb89y">
              <SidebarSubItem label="Case Type" active={activeMenu === "Case Type"} onClick={() => handleMenuClick("Case Type")} data-id="element-36" data-mp="2kfmkrz20uilw" />
              <SidebarSubItem label="Reporting Agency" active={activeMenu === "Reporting Agency"} onClick={() => handleMenuClick("Reporting Agency")} data-id="element-37" data-mp="30uc7g0mru4kr" />
            </SidebarGroup>
          </nav>
        </div>

        <div className="p-4 border-t border-gray-200" data-id="element-38" data-mp="2qvwfxwju0taq">
          <div className="flex items-center gap-3" data-id="element-39" data-mp="2ly4ot4r0hb7l">
            <div className="w-10 h-10 bg-blue-800 rounded-md flex items-center justify-center text-white font-bold" data-id="element-40" data-mp="2lxl6hmxg8kt1">
              A
            </div>
            <div data-id="element-41" data-mp="2d62rvgg5id22">
              <div className="text-sm font-semibold text-gray-800" data-id="element-42" data-mp="2d5j77rz49akc">Admin</div>
              <div className="text-xs text-gray-500" data-id="element-43" data-mp="286gdh3ol6qro">System Admin</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden" data-id="element-44" data-mp="239g7stvnfq13">
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0" data-id="element-45" data-mp="1y78rxt7p7pth">
          <div className="flex items-center gap-2" data-id="element-46" data-mp="1u91kjoz4k0uy">
            <div className="px-3 py-1.5 border border-gray-200 rounded-md flex items-center gap-2 text-sm font-medium text-gray-700 bg-gray-50" data-id="element-47" data-mp="9pnyeq318hwz">
              <LayoutDashboard size={16} className="text-gray-500" data-id="element-48" data-mp="dsknmzn2vc9w" />
              Data Analytics Center
            </div>
          </div>
          <div className="flex items-center gap-4" data-id="element-49" data-mp="nofegi1ggdnr">
            <button className="text-gray-500 hover:text-gray-700 relative" data-id="element-50" data-mp="nnw3xme4wrjr">
              <Bell size={20} data-id="element-51" data-mp="3m7m7s1fzmlzm" />
              <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" data-id="element-52" data-mp="3m4866vf5egt8"></span>
            </button>
            <div className="h-6 w-px bg-gray-200" data-id="element-53" data-mp="3r31n6xtn8py5"></div>
            <button className="flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm font-medium" data-id="element-54" data-mp="3w3pb3amksqo5">
              <LogOut size={18} data-id="element-55" data-mp="4re6qpginzg4" />
              Logout
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-auto p-6" data-id="element-56" data-mp="38ewkltoq3b0x">
          {renderContent()}
        </div>
      </main>
    </div>;
}
function SidebarItem({
  icon,
  label,
  active,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return <button onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${active ? "bg-blue-800 text-white" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"}`} data-id="element-57" data-mp="2k4djmdi9t34b">
      {icon}
      <span className="text-sm font-medium" data-id="element-58" data-mp="1whf53ldpo591">{label}</span>
    </button>;
}
function SidebarGroup({
  icon,
  label,
  isExpanded,
  onToggle,
  children
}: {
  icon: React.ReactNode;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return <div className="flex flex-col" data-id="element-59" data-mp="2b59wmyfzg3kj">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors text-gray-600 hover:bg-gray-100 hover:text-gray-900" data-id="element-60" data-mp="2b6l8lljl0hmq">
        <div className="flex items-center gap-3" data-id="element-61" data-mp="1inxvasatu6w7">
          {icon}
          <span className="text-sm font-medium" data-id="element-62" data-mp="3w00nlmkabm7x">{label}</span>
        </div>
        <ChevronDown size={16} className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} data-id="element-63" data-mp="3vx5z9jy5hh98" />
      </button>
      {isExpanded && <div className="ml-11 mt-1 space-y-1" data-id="element-64" data-mp="3mxrd2wp0nsuj">
          {children}
        </div>}
    </div>;
}
function SidebarSubItem({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 rounded-md transition-colors ${active ? "text-blue-700 font-medium bg-blue-50" : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"}`} data-id="element-65" data-mp="3d27lunledic4">
      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-blue-600" : "bg-gray-300"}`} data-id="element-66" data-mp="2zaasyhbwn4eo"></span>
      {label}
    </button>;
}